import { getLogicEndpoint } from "./endpoints";
import { isLocalMode, localFetch } from "./localRuntimeClient";

export type AppContext = {
  projectName: string;
  appName: string;
  getToken: () => Promise<string | null>;
};

export const getAppUrl = (opts: { projectName: string; appName: string }): string => {
  return `${getLogicEndpoint()}/automationProjects/${encodeURIComponent(opts.projectName)}/applications/${encodeURIComponent(opts.appName)}`;
};

export const getNewVfsUrl = (opts: { projectName: string; appName: string }): string => {
  return `${getAppUrl(opts)}/vfs`;
};

const isVfsUrl = (url: string): boolean => url.includes('/vfs');

/** Normalize a VFS URL to a cache key (strip query params) */
const vfsCacheKey = (url: string): string => url.split('?')[0] ?? url;
// Cache of ETags from VFS responses, keyed by URL path
const vfsETagCache = new Map<string, string>();

/**
 * Fetch the ETag for a VFS resource by doing a HEAD request.
 * Returns the ETag string or '*' if the resource doesn't exist (for new files).
 */
const getVfsETag = async (url: string, headers: Record<string, string>): Promise<string> => {
  // Check cache first
  const cached = vfsETagCache.get(vfsCacheKey(url));
  if (cached) {
    return cached;
  }

  try {
    const response = await fetch(url, {
      method: 'HEAD',
      headers,
      cache: 'no-store',
    });
    if (response.ok) {
      const etag = response.headers.get('etag') ?? '*';
      vfsETagCache.set(vfsCacheKey(url), etag);
      return etag;
    }
    return '*';
  } catch {
    return '*';
  }
};

/**
 * Fetch wrapper for VFS Api.
 *
 * - In local mode: no auth, routes to localhost
 * - In cloud mode: uses bearer token and full app URL
 * - For VFS PUT/DELETE: fetches current ETag first and sends If-Match header
 */
const vfsFetch = async <T>(
  url: string,
  options: RequestInit = {},
): Promise<T> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> ?? {}),
  };

  // VFS PUT/DELETE requires If-Match with the current ETag
  const method = (options.method ?? 'GET').toUpperCase();
  if ((method === 'PUT' || method === 'DELETE')) {
    const etag = await getVfsETag(url, headers);
    headers['If-Match'] = etag;
  }

  const response = await fetch(url, {
    ...options,
    headers,
    cache: 'no-store',
  });

  if (!response.ok) {
    // On 412 for VFS, invalidate cached ETag and retry once
    if (response.status === 412) {
      vfsETagCache.delete(vfsCacheKey(url));
      delete headers['If-Match']; // remove stale before HEAD request
      const freshEtag = await getVfsETag(url, { ...headers });
      headers['If-Match'] = freshEtag;

      const retryResponse = await fetch(url, { ...options, headers, cache: 'no-store' });
      if (!retryResponse.ok) {
        const errorText = await retryResponse.text();
        throw new VfsFetchError(retryResponse.status, errorText);
      }

      // Cache new ETag from retry response
      const retryEtag = retryResponse.headers.get('etag');
      if (retryEtag) {
        vfsETagCache.set(vfsCacheKey(url), retryEtag);
      }

      const retryText = await retryResponse.text();
      if (!retryText) return {} as T;
      return JSON.parse(retryText);
    }

    const errorText = await response.text();
    throw new VfsFetchError(response.status, errorText);
  }

  // Cache ETag from successful VFS write responses for next call
  if ((method === 'PUT' || method === 'DELETE')) {
    const newEtag = response.headers.get('etag');
    if (newEtag) {
      vfsETagCache.set(vfsCacheKey(url), newEtag);
    } else {
      // ETag changed, invalidate cache so next call does a fresh HEAD
      vfsETagCache.delete(vfsCacheKey(url));
    }
  }

  // Handle empty responses
  const text = await response.text();
  if (!text) {
    return {} as T;
  }

  return JSON.parse(text);
};

/**
 * Typed error for VFS fetch failures with structured status and body.
 */
export class VfsFetchError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: string,
  ) {
    super(`VFS API request failed: ${status} ${body}`);
    this.name = 'VfsFetchError';
  }
}

/**
 * Fetch wrapper that supports both local and cloud modes.
 * In local mode, skips auth.
 * In cloud mode for ACA direct-plane calls, uses master key via acaFetch.
 * For ARM management calls, uses bearer token.
 */
export const appFetch = async <T>(url: string, accessToken: string, options?: RequestInit, defaultReturn?: T): Promise<T> => {
  if (isLocalMode()) {
    return localFetch<T>(url, {
      method: (options?.method as 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH') ?? 'GET',
      body: options?.body ? JSON.parse(options.body as string) : undefined,
      ...(options?.signal ? { signal: options.signal } : {}),
    });
  }

  const headers: Record<string, string> = {
    ...(options?.headers as Record<string, string> ?? {}),
    Authorization: `Bearer ${accessToken}`,
    'Content-Type': 'application/json',
  };

  if (isVfsUrl(url)) {
    try {
      return await vfsFetch<T>(url, {
        ...options,
        headers: {
          ...(options?.headers ?? {}),
          Authorization: `Bearer ${accessToken}`
        },
        cache: 'no-store'
      });
    } catch (error) {
      // Return default value for 404 errors (e.g., draft files that don't exist)
      if (defaultReturn !== undefined && error instanceof VfsFetchError && error.status === 404) {
        return defaultReturn;
      }
      throw error;
    }
  }

  // ARM calls use bearer token
  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new AppFetchError(response.status, errorText);
  }

  // Handle empty responses (e.g., 204 No Content, empty body)
  const text = await response.text();
  if (!text) {
    return (defaultReturn ?? {}) as T;
  }

  return JSON.parse(text);
};

/**
 * Typed error for VFS fetch failures with structured status and body.
 */
export class AppFetchError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: string,
  ) {
    super(`Application API request failed: ${status} ${body}`);
    this.name = 'AppFetchError';
  }
}
